/**
 * REST единого движка чатов v2 — `/v2/chats` (инструкция бэка, части 1–6).
 *
 * Все маршруты — по id чата. Чат появляется при первом открытии: у строки
 * списка с `chatId: null` id сначала берём через `resolveChat`. Лента листается
 * курсором `before` (RFC3339 самого старого показанного), новые сверху.
 *
 * Имён авторов в ответах нет — только authorId, склеиваем со справочником
 * пользователей `/v2/users`.
 */
import { API_BASE_URL, getAccessToken, http } from '@/shared/api';

import type {
  ChatFile,
  ChatKind,
  ChatListItem,
  ChatMessage,
  ChatRef,
  ChatSendPayload,
  ChatSettings,
  ChatTranscriptLine,
} from '../model/types';

const CHATS = '/v2/chats';
const chat = (chatId: string) => `${CHATS}/${encodeURIComponent(chatId)}`;
const msg = (chatId: string, messageId: string) =>
  `${chat(chatId)}/messages/${encodeURIComponent(messageId)}`;

/* ------------------------------------------------------ нормализация --- */

const NO_SETTINGS: ChatSettings = {
  isMuted: false,
  isArchived: false,
  isPinned: false,
  isFavorite: false,
};

/**
 * reactions, mentions и files бэк обещает массивами, но в кадре WS сообщение
 * описано короче (id, chatId, authorId, body, …). Приводим к одной форме,
 * чтобы ниже по коду не проверять каждое поле.
 */
export function normalizeMessage(message: ChatMessage): ChatMessage {
  return {
    ...message,
    body: message.body ?? '',
    reactions: message.reactions ?? [],
    mentions: message.mentions ?? [],
    files: message.files ?? [],
  };
}

function normalizeItem(item: ChatListItem): ChatListItem {
  return {
    ...item,
    unreadCount: item.unreadCount ?? 0,
    settings: { ...NO_SETTINGS, ...item.settings },
  };
}

/* ------------------------------------------------------------ список --- */

/**
 * Чаты, где я участник. Без kind — отделы, проекты и личные; чаты задач
 * отдельным запросом (`kind: 'task'`), в общем списке их нет. Сортировку
 * (закреплённые, потом по последнему сообщению) делает сервер.
 */
export async function fetchChats(kind?: ChatKind): Promise<ChatListItem[]> {
  const { data } = await http.get<{ items?: ChatListItem[] }>(CHATS, {
    params: kind ? { kind } : undefined,
  });

  return (data.items ?? []).map(normalizeItem);
}

/** Открыть чат отдела, проекта или задачи: создаёт при первом вызове. */
export async function resolveChat(
  kind: Exclude<ChatKind, 'direct'>,
  subjectId: string
): Promise<ChatRef> {
  const { data } = await http.get<ChatRef>(
    `${CHATS}/by/${kind}/${encodeURIComponent(subjectId)}`
  );

  return data;
}

export async function fetchChat(chatId: string): Promise<ChatRef> {
  const { data } = await http.get<ChatRef>(chat(chatId));

  return data;
}

/** Личка 1:1 — тот же id, с какой стороны ни открывай. С собой — 400. */
export async function openDirectChat(userId: string): Promise<ChatRef> {
  const { data } = await http.post<ChatRef>(`${CHATS}/direct`, { userId });

  return data;
}

/* ------------------------------------------------------------- лента --- */

export async function fetchMessages(
  chatId: string,
  params: { before?: string; limit?: number } = {}
): Promise<ChatMessage[]> {
  const { data } = await http.get<{ result?: ChatMessage[] }>(
    `${chat(chatId)}/messages`,
    { params }
  );

  return (data.result ?? []).map(normalizeMessage);
}

export async function sendMessage(
  chatId: string,
  payload: ChatSendPayload
): Promise<ChatMessage> {
  const { data } = await http.post<ChatMessage>(
    `${chat(chatId)}/messages`,
    payload
  );

  return normalizeMessage(data);
}

/** Только автор (403), удалённое не правится (409). */
export async function editMessage(
  chatId: string,
  messageId: string,
  body: string
): Promise<ChatMessage> {
  const { data } = await http.patch<ChatMessage>(msg(chatId, messageId), {
    body,
  });

  return normalizeMessage(data);
}

/** Мягкое удаление, только своё. */
export function deleteMessage(chatId: string, messageId: string) {
  return http.delete(msg(chatId, messageId));
}

/** До 100 за раз; в ответе — реально удалённые. */
export async function deleteMessages(
  chatId: string,
  messageIds: string[]
): Promise<string[]> {
  const { data } = await http.post<{ deleted?: string[] }>(
    `${chat(chatId)}/messages/delete`,
    { messageIds }
  );

  return data.deleted ?? [];
}

/** Копия с forwardedFromId; нужен доступ к обоим чатам, в тот же — 409. */
export async function forwardMessage(
  chatId: string,
  messageId: string,
  targetChatId: string
): Promise<ChatMessage> {
  const { data } = await http.post<ChatMessage>(
    `${msg(chatId, messageId)}/forward`,
    { targetChatId }
  );

  return normalizeMessage(data);
}

/** «Прочитано до сейчас»: unreadCount в списке и сводке обнуляется. */
export function markRead(chatId: string) {
  return http.post(`${chat(chatId)}/read`);
}

export async function searchMessages(
  chatId: string,
  q: string,
  limit = 20
): Promise<ChatMessage[]> {
  const { data } = await http.get<{ result?: ChatMessage[] }>(
    `${chat(chatId)}/messages/search`,
    { params: { q, limit } }
  );

  return (data.result ?? []).map(normalizeMessage);
}

/* --------------------------------------------- реакции и закрепления --- */

/** Переключатель: тот же эмодзи повторно снимает реакцию. */
export async function toggleReaction(
  chatId: string,
  messageId: string,
  emoji: string
): Promise<{ emoji?: string; reacted?: boolean }> {
  const { data } = await http.post<{ emoji?: string; reacted?: boolean }>(
    `${msg(chatId, messageId)}/reactions`,
    { emoji }
  );

  return data;
}

export function pinMessage(chatId: string, messageId: string) {
  return http.post(`${msg(chatId, messageId)}/pin`);
}

export function unpinMessage(chatId: string, messageId: string) {
  return http.delete(`${msg(chatId, messageId)}/pin`);
}

export async function fetchPins(chatId: string): Promise<ChatMessage[]> {
  const { data } = await http.get<{ result?: ChatMessage[] }>(
    `${chat(chatId)}/pins`
  );

  return (data.result ?? []).map(normalizeMessage);
}

/* ------------------------------------------------------- настройки --- */

/**
 * Частичное обновление: слать только меняемые поля. isMuted: false снимает и
 * mute, и срок. Есть только у созданного чата — строку с chatId: null
 * сначала резолвим.
 */
export async function updateSettings(
  chatId: string,
  patch: Partial<ChatSettings>
): Promise<void> {
  await http.patch(`${chat(chatId)}/settings`, patch);
}

/* ----------------------------------------------------------- файлы --- */

/**
 * Загрузка до отправки: потом id уходит в fileIds сообщения. У голосового —
 * durationMs; отдельного маршрута для голосовых нет. Хранилище не настроено —
 * 503.
 */
export async function uploadFile(
  chatId: string,
  file: Blob,
  options: { name?: string; durationMs?: number } = {}
): Promise<ChatFile> {
  const form = new FormData();
  const name = options.name ?? (file instanceof File ? file.name : 'file');
  form.append('file', file, name);
  if (options.durationMs !== undefined) {
    form.append(
      'durationMs',
      String(Math.max(0, Math.round(options.durationMs)))
    );
  }
  const { data } = await http.post<ChatFile>(`${chat(chatId)}/files`, form);

  return data;
}

/** Расшифровка вложения (для файлов сообщений чата задачи). */
export function saveTranscript(
  chatId: string,
  fileId: string,
  transcript: ChatTranscriptLine[]
) {
  return http.put(
    `${chat(chatId)}/files/${encodeURIComponent(fileId)}/transcript`,
    { transcript }
  );
}

/**
 * Ссылка для `<img>`, `<audio>` и `<a href>`: заголовок туда не поставить,
 * поэтому токен в query. thumb — превью картинки в JPEG, вписанное в квадрат
 * px; у не-картинок игнорируется.
 */
export function fileDownloadUrl(
  chatId: string,
  fileId: string,
  options: { thumb?: number } = {}
): string {
  const url = new URL(
    `${API_BASE_URL.replace(/\/+$/, '')}${chat(chatId)}/files/${encodeURIComponent(fileId)}/download`,
    window.location.origin
  );
  const token = getAccessToken();
  if (token) {
    url.searchParams.set('token', token);
  }
  if (options.thumb) {
    url.searchParams.set('thumb', String(options.thumb));
  }

  return url.toString();
}

/* ---------------------------------------------------------- сокет --- */

/**
 * Адрес единого сокета: один на пользователя, события всех его чатов. Токен —
 * в query, заголовок браузерному WebSocket не выставить.
 */
export function chatsSocketUrl(token: string): string {
  const url = new URL(
    `${API_BASE_URL.replace(/\/+$/, '')}${CHATS}/ws`,
    window.location.origin
  );
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);

  return url.toString();
}
